import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Avatar, Chip, Stack, Skeleton, Divider, alpha,
  Button, TextField, Autocomplete, IconButton, CircularProgress, MenuItem,
  FormHelperText,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import {
  useGetMyProfileQuery,
  useGetMyProfileEditOptionsQuery,
  useUpdateMyProfileMutation,
} from "../../Api/attoDeskApi";
import { C } from "./shared/palette";
import { useNotifier } from "../../Core/Notifier";
import { useConfirm } from "../../Components/Confirm";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumberForDisplay,
} from "../../Components/Validation/validationUtils";

/**
 * Staff Portal → My Profile.
 *
 * View mode is the read-only premium layout. When the acting staff
 * clicks "Edit Profile" in the hero, the Contact Information and
 * Teaching Profile cards flip to inline editors — Account stays
 * read-only (username / reference_id / portal_status / last_login
 * are system-controlled).
 *
 * Backend endpoint GET /api/staff-portal/profile is auth-derived —
 * no staff_id in URL/body, no IDOR surface. The PATCH endpoint
 * accepts a strict whitelist (email, phone, alt_phone, location_ids[],
 * subject_grade_ids[]); every other field the client might inject
 * (role, status, username, password, salary, tenant, etc.) is
 * silently ignored server-side.
 */

// ── Field shapes (mirror backend response) ──
interface ProfileStaff {
  id: number;
  reference_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  alt_phone: string | null;
  role_label: string;
  status: string;
  avatar_url: string | null;
  username: string | null;
  portal_status: "active" | "invitation_pending" | "suspended" | "no_login";
  last_login_at: string | null;
}
interface ProfileLocation { id: number; name: string; }
interface ProfileGrade { grade_id: number; grade_name: string; subject_grade_id?: number; }
interface ProfileSubject { subject_id: number; subject_name: string; grades: ProfileGrade[]; }
interface ProfileResponse {
  staff: ProfileStaff;
  locations: ProfileLocation[];
  teaching_assignments: ProfileSubject[];
  has_aba_caseload: boolean;
}
interface CatalogSubject {
  subject_id: number;
  subject_name: string;
  grades: { grade_id: number; grade_name: string; subject_grade_id: number }[];
}
interface EditOptionsResponse {
  subjects: CatalogSubject[];
  locations: ProfileLocation[];
}

const NOT_PROVIDED = "Not provided";
const NO_ASSIGN    = "No teaching assignments";
const NO_LOC       = "No location assigned";

// Grade-label helper matches the read-mode formatting so edit and
// view show the same "Grade 5" chip text for numeric grade names.
const formatGradeLabel = (name: string): string =>
  /^(pre-?k|jk|sk|adult|\d+)$/i.test((name || "").trim())
    ? `Grade ${name}` : name;

// ── Section card (matches ParentRequests/MessageTemplates visual language) ──
const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  tone: keyof typeof C;
  children: React.ReactNode;
}> = ({ title, icon, tone, children }) => {
  const t = C[tone];
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.06)",
        boxShadow: "0 1px 4px rgba(15,23,42,0.03)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 2, borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
        <Box
          sx={{
            width: 32, height: 32, borderRadius: "10px",
            bgcolor: t.bg, color: t.fg,
            display: "flex", alignItems: "center", justifyContent: "center",
            "& svg": { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: C.navy.fg }}>{title}</Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 2.25 }}>{children}</Box>
    </Box>
  );
};

// ── Info row (label + value; renders skeleton while loading) ──
const InfoRow: React.FC<{
  label: string;
  value: string | null | undefined;
  icon?: React.ReactNode;
  emptyLabel?: string;
  loading?: boolean;
}> = ({ label, value, icon, emptyLabel = NOT_PROVIDED, loading }) => {
  const isEmpty = !loading && (!value || String(value).trim() === "");
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.25 }}>
      {icon && (
        <Box sx={{ color: "text.disabled", display: "flex", pt: 0.4, "& svg": { fontSize: 18 } }}>
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={180} sx={{ mt: 0.25 }} />
        ) : (
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 500,
              color: isEmpty ? "text.disabled" : C.navy.fg,
              fontStyle: isEmpty ? "italic" : "normal",
              wordBreak: "break-word",
            }}
          >
            {isEmpty ? emptyLabel : value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ── Edit-mode row — same visual language as InfoRow (icon + label
//    stripe) but the value is replaced by a compact input so the
//    card doesn't look like a separate form page.
const EditRow: React.FC<{
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  error?: string;
}> = ({ label, icon, children, error }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.25 }}>
    {icon && (
      <Box sx={{ color: "text.disabled", display: "flex", pt: 0.4, "& svg": { fontSize: 18 } }}>
        {icon}
      </Box>
    )}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
        {label}
      </Typography>
      {children}
      {error && (
        <FormHelperText error sx={{ ml: 0, mt: 0.25, fontSize: 11 }}>{error}</FormHelperText>
      )}
    </Box>
  </Box>
);

const grpChip = (label: string, tone: keyof typeof C) => {
  const t = C[tone];
  return (
    <Chip
      key={label + tone}
      size="small"
      label={label}
      sx={{
        height: 24,
        fontSize: 11.5,
        fontWeight: 700,
        bgcolor: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
      }}
    />
  );
};

// ── Portal status → chip meta ──
const PORTAL_STATUS_META: Record<ProfileStaff["portal_status"], { tone: keyof typeof C; label: string }> = {
  active:              { tone: "green",  label: "Active" },
  invitation_pending:  { tone: "amber",  label: "Invitation Pending" },
  suspended:           { tone: "rose",   label: "Suspended" },
  no_login:            { tone: "navy",   label: "No Login" },
};

// ── Draft state (edit mode). Held locally so cancel restores original.
interface DraftState {
  email: string;
  phone: string;         // 10 raw digits (formatter strips non-digits on change)
  alt_phone: string;
  location_ids: number[];
  // subject_id → set of subject_grade_ids selected
  subjects: { subject_id: number; subject_grade_ids: number[] }[];
}

const draftEqual = (a: DraftState, b: DraftState): boolean => {
  if (a.email !== b.email) return false;
  if (a.phone !== b.phone) return false;
  if (a.alt_phone !== b.alt_phone) return false;
  const la = [...a.location_ids].sort((x, y) => x - y).join(",");
  const lb = [...b.location_ids].sort((x, y) => x - y).join(",");
  if (la !== lb) return false;
  // Flatten to a comparable list of pair ids — subject-level identity
  // is derivable from the pair set (a subject exists iff at least one
  // pair references it), so we compare pair sets directly.
  const flat = (d: DraftState) =>
    d.subjects.flatMap((s) => s.subject_grade_ids).sort((x, y) => x - y).join(",");
  return flat(a) === flat(b);
};

const buildDraft = (p: ProfileResponse): DraftState => ({
  email: p.staff.email ?? "",
  phone: (p.staff.phone ?? "").replace(/\D/g, ""),
  alt_phone: (p.staff.alt_phone ?? "").replace(/\D/g, ""),
  location_ids: p.locations.map((l) => l.id),
  subjects: p.teaching_assignments.map((s) => ({
    subject_id: s.subject_id,
    subject_grade_ids: s.grades
      .map((g) => g.subject_grade_id)
      .filter((v): v is number => typeof v === "number"),
  })),
});

const MyProfile: React.FC = () => {
  const { data, isLoading, isError, refetch } = useGetMyProfileQuery();
  const notifier = useNotifier();
  const confirm = useConfirm();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [errors, setErrors] = useState<{ email?: string; phone?: string; alt_phone?: string }>({});

  // Options are fetched only once the user actually enters edit mode
  // (skip=true otherwise) — keeps the view-mode network cost the same
  // as before this change.
  const {
    data: optionsData,
    isLoading: optionsLoading,
  } = useGetMyProfileEditOptionsQuery(undefined, { skip: !editing });
  const [updateProfile, { isLoading: isSaving }] = useUpdateMyProfileMutation();

  const payload = useMemo<ProfileResponse | null>(() => {
    const d: any = (data as any)?.data;
    return d && d.staff ? (d as ProfileResponse) : null;
  }, [data]);
  const options = useMemo<EditOptionsResponse | null>(() => {
    const d: any = (optionsData as any)?.data;
    return d && Array.isArray(d.subjects) ? (d as EditOptionsResponse) : null;
  }, [optionsData]);

  const staff = payload?.staff;
  const fullName = staff
    ? [staff.first_name, staff.last_name].filter(Boolean).join(" ").trim() || "Staff"
    : "";
  const initial = (fullName || "?").charAt(0).toUpperCase();
  const portalMeta = staff ? PORTAL_STATUS_META[staff.portal_status] : PORTAL_STATUS_META.no_login;
  const lastLoginLabel = staff?.last_login_at ? dayjs(staff.last_login_at).format("MMM D, YYYY h:mm A") : "";

  const originalDraft = useMemo<DraftState | null>(
    () => (payload ? buildDraft(payload) : null),
    [payload]
  );
  const isDirty = !!(draft && originalDraft && !draftEqual(draft, originalDraft));

  // Prime the draft when entering edit mode. Guard against re-priming
  // while the user is actively editing (payload could refetch under us).
  useEffect(() => {
    if (editing && !draft && payload) setDraft(buildDraft(payload));
  }, [editing, draft, payload]);

  const handleEnterEdit = () => {
    if (!payload) return;
    setDraft(buildDraft(payload));
    setErrors({});
    setEditing(true);
  };

  const handleCancel = async () => {
    if (isSaving) return;
    if (isDirty) {
      const ok = await confirm({
        title: "Discard changes?",
        message: "You have unsaved changes on your profile. Discard them and return to view mode?",
        confirmText: "Discard Changes",
        cancelText: "Keep Editing",
        variant: "danger",
      });
      if (!ok) return;
    }
    setDraft(null);
    setErrors({});
    setEditing(false);
  };

  const validateLocal = (d: DraftState): boolean => {
    const e: typeof errors = {};
    const em = validateEmail(d.email);
    if (em) e.email = em;
    const p = validatePhoneNumber(d.phone);
    if (p) e.phone = p;
    const a = validatePhoneNumber(d.alt_phone);
    if (a) e.alt_phone = a;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!validateLocal(draft)) return;

    // Flatten the subject drafts to the wire-format the backend
    // accepts (pair-id array only). subject_id is implicit.
    const subject_grade_ids = Array.from(
      new Set(draft.subjects.flatMap((s) => s.subject_grade_ids))
    );

    try {
      const res: any = await updateProfile({
        email: draft.email.trim() || null,
        phone: draft.phone || null,
        alt_phone: draft.alt_phone || null,
        location_ids: draft.location_ids,
        subject_grade_ids,
      }).unwrap();

      if (res?.status === false || res?.code >= 400) {
        // Server returned a normalized failure shape.
        const msg = res?.message || "Unable to save profile.";
        notifier.showErrorMessage(msg);
        return; // stay in edit mode, preserve draft
      }

      notifier.showMessage("Profile updated.");
      // Refetch view-mode payload; the mutation already invalidates
      // "team" but we call refetch to guarantee freshness before we
      // exit edit mode (no optimistic UI per spec).
      await refetch();
      setDraft(null);
      setErrors({});
      setEditing(false);
    } catch (err: any) {
      const errData = err?.data;
      // Field-level 422 errors get surfaced next to their inputs.
      if (errData?.errors && typeof errData.errors === "object") {
        const fieldErrs: typeof errors = {};
        const fe = errData.errors;
        if (fe.email?.[0])     fieldErrs.email = fe.email[0];
        if (fe.phone?.[0])     fieldErrs.phone = fe.phone[0];
        if (fe.alt_phone?.[0]) fieldErrs.alt_phone = fe.alt_phone[0];
        setErrors(fieldErrs);
        notifier.showErrorMessage(errData.message || "Please fix the highlighted fields.");
      } else {
        notifier.showErrorMessage(errData?.message || err?.message || "Unable to save profile.");
      }
    }
  };

  // ── Draft mutators ─────────────────────────────────────────────
  const patch = (p: Partial<DraftState>) =>
    setDraft((d) => (d ? { ...d, ...p } : d));

  const toggleGrade = (subjectId: number, pairId: number) => {
    setDraft((d) => {
      if (!d) return d;
      const subjects = d.subjects.map((s) => {
        if (s.subject_id !== subjectId) return s;
        const has = s.subject_grade_ids.includes(pairId);
        return {
          ...s,
          subject_grade_ids: has
            ? s.subject_grade_ids.filter((v) => v !== pairId)
            : [...s.subject_grade_ids, pairId],
        };
      });
      return { ...d, subjects };
    });
  };

  const removeSubject = (subjectId: number) => {
    setDraft((d) => (d ? { ...d, subjects: d.subjects.filter((s) => s.subject_id !== subjectId) } : d));
  };

  const addSubject = (subjectId: number) => {
    if (!subjectId) return;
    setDraft((d) => {
      if (!d) return d;
      if (d.subjects.some((s) => s.subject_id === subjectId)) return d; // no duplicates
      return { ...d, subjects: [...d.subjects, { subject_id: subjectId, subject_grade_ids: [] }] };
    });
  };

  const locationOptions = options?.locations ?? [];
  const subjectOptions = options?.subjects ?? [];
  const usedSubjectIds = new Set(draft?.subjects.map((s) => s.subject_id) ?? []);
  const addableSubjects = subjectOptions.filter((s) => !usedSubjectIds.has(s.subject_id));

  const [pendingAddSubjectId, setPendingAddSubjectId] = useState<number | "">("");

  // Displayed phone values in view mode use the shared display
  // formatter so the read-only value looks like the rest of Step Up.
  const displayPhone    = formatPhoneNumberForDisplay(staff?.phone ?? "");
  const displayAltPhone = formatPhoneNumberForDisplay(staff?.alt_phone ?? "");

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh", mx: -3, mt: -3 }}>
      {/* ══════════ HERO ══════════ */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0d1b2a 0%, #1b263b 60%, #243447 100%)",
          borderRadius: "16px",
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute", top: 0, right: 0,
            width: "40%", height: "100%",
            background: "radial-gradient(ellipse at top right, rgba(59,130,246,0.15), transparent 70%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, gap: 2.5, position: "relative", zIndex: 1, flexDirection: { xs: "column", md: "row" } }}>
          {isLoading ? (
            <Skeleton variant="circular" width={72} height={72} sx={{ bgcolor: "rgba(255,255,255,0.12)" }} />
          ) : (
            <Avatar
              src={staff?.avatar_url || undefined}
              sx={{
                width: 72, height: 72, fontSize: 28, fontWeight: 800,
                bgcolor: alpha("#3b82f6", 0.3), color: "#fff",
                border: "3px solid rgba(255,255,255,0.2)",
              }}
            >
              {initial}
            </Avatar>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isLoading ? (
              <>
                <Skeleton variant="text" width={220} height={34} sx={{ bgcolor: "rgba(255,255,255,0.12)" }} />
                <Skeleton variant="text" width={280} height={22} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
                  {fullName}
                </Typography>
                <Stack direction="row" spacing={1} mt={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={staff?.role_label ?? "Staff"}
                    size="small"
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "rgba(255,255,255,0.14)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}
                  />
                  <Chip
                    label={portalMeta.label}
                    size="small"
                    sx={{
                      height: 22, fontSize: 11, fontWeight: 700,
                      bgcolor: alpha(C[portalMeta.tone].fg, 0.22),
                      color: "#fff",
                      border: `1px solid ${alpha(C[portalMeta.tone].fg, 0.5)}`,
                    }}
                  />
                  {staff?.email && (
                    <Chip
                      icon={<EmailOutlinedIcon sx={{ fontSize: "13px !important", color: "rgba(255,255,255,0.7) !important" }} />}
                      label={staff.email}
                      size="small"
                      sx={{ height: 22, fontSize: 11, fontWeight: 500, bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)", "& .MuiChip-label": { px: 0.75 } }}
                    />
                  )}
                  {staff?.phone && (
                    <Chip
                      icon={<PhoneOutlinedIcon sx={{ fontSize: "13px !important", color: "rgba(255,255,255,0.7) !important" }} />}
                      label={displayPhone}
                      size="small"
                      sx={{ height: 22, fontSize: 11, fontWeight: 500, bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)", "& .MuiChip-label": { px: 0.75 } }}
                    />
                  )}
                </Stack>
              </>
            )}
          </Box>

          {/* Edit / Save+Cancel controls. Only visible once the profile
              payload has arrived so the button doesn't flash before we
              have anything to edit. */}
          {!isLoading && payload && (
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0, alignSelf: { xs: "stretch", md: "center" } }}>
              {editing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    disabled={isSaving}
                    variant="outlined"
                    size="small"
                    startIcon={<CloseIcon />}
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      borderColor: "rgba(255,255,255,0.35)",
                      "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
                      textTransform: "none", fontWeight: 700,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    variant="contained"
                    size="small"
                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{
                      bgcolor: "#3b82f6", color: "#fff",
                      "&:hover": { bgcolor: "#2563eb" },
                      textTransform: "none", fontWeight: 700,
                    }}
                  >
                    {isSaving ? "Saving…" : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEnterEdit}
                  variant="contained"
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                    textTransform: "none", fontWeight: 700,
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {isError && (
        <Box
          sx={{
            mb: 3, px: 2.5, py: 2,
            bgcolor: C.rose.bg, border: `1px solid ${C.rose.border}`,
            color: C.rose.fg, borderRadius: "12px", fontSize: 13, fontWeight: 600,
          }}
        >
          Unable to load your profile right now. Please try again in a moment.
        </Box>
      )}

      {/* ══════════ CARD GRID ══════════ */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
        {/* ── Contact Information ── */}
        <SectionCard title="Contact Information" icon={<ContactPageOutlinedIcon />} tone="blue">
          {editing && draft ? (
            <Stack divider={<Divider />}>
              <EditRow label="Email" icon={<EmailOutlinedIcon />} error={errors.email}>
                <TextField
                  fullWidth size="small" type="email"
                  value={draft.email}
                  onChange={(e) => patch({ email: e.target.value })}
                  placeholder="you@example.com"
                  inputProps={{ maxLength: 254 }}
                  error={!!errors.email}
                />
              </EditRow>
              <EditRow label="Mobile" icon={<PhoneOutlinedIcon />} error={errors.phone}>
                <TextField
                  fullWidth size="small"
                  value={formatPhoneNumberForDisplay(draft.phone) || draft.phone}
                  onChange={(e) => patch({ phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  placeholder="10-digit mobile number"
                  inputProps={{ inputMode: "numeric", maxLength: 14 }}
                  error={!!errors.phone}
                />
              </EditRow>
              <EditRow label="Alt Phone" icon={<PhoneOutlinedIcon />} error={errors.alt_phone}>
                <TextField
                  fullWidth size="small"
                  value={formatPhoneNumberForDisplay(draft.alt_phone) || draft.alt_phone}
                  onChange={(e) => patch({ alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  placeholder="Optional"
                  inputProps={{ inputMode: "numeric", maxLength: 14 }}
                  error={!!errors.alt_phone}
                />
              </EditRow>
            </Stack>
          ) : (
            <Stack divider={<Divider />}>
              <InfoRow label="Email"     value={staff?.email}      icon={<EmailOutlinedIcon />} loading={isLoading} />
              <InfoRow label="Mobile"    value={displayPhone}      icon={<PhoneOutlinedIcon />} loading={isLoading} />
              <InfoRow label="Alt Phone" value={displayAltPhone}   icon={<PhoneOutlinedIcon />} loading={isLoading} />
            </Stack>
          )}
        </SectionCard>

        {/* ── Teaching Profile ── */}
        <SectionCard title="Teaching Profile" icon={<SchoolOutlinedIcon />} tone="indigo">
          {isLoading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rectangular" height={26} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={26} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={26} sx={{ borderRadius: 1 }} />
            </Stack>
          ) : editing && draft ? (
            <Stack spacing={2}>
              {/* Locations selector */}
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 14 }} /> Locations
                </Typography>
                <Autocomplete
                  multiple size="small"
                  loading={optionsLoading}
                  options={locationOptions}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={locationOptions.filter((o) => draft.location_ids.includes(o.id))}
                  onChange={(_e, val) => patch({ location_ids: val.map((v) => v.id) })}
                  renderTags={(val, getTagProps) =>
                    val.map((v, i) => (
                      <Chip
                        {...getTagProps({ index: i })}
                        key={v.id}
                        label={v.name}
                        size="small"
                        sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: C.teal.bg, color: C.teal.fg, border: `1px solid ${C.teal.border}` }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder={draft.location_ids.length ? "" : "Select one or more locations"} />
                  )}
                />
              </Box>
              <Divider />
              {/* Subjects & grades editor */}
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <SchoolOutlinedIcon sx={{ fontSize: 14 }} /> Subjects &amp; Grades
                </Typography>
                <Stack spacing={1.5}>
                  {draft.subjects.map((s) => {
                    const cat = subjectOptions.find((c) => c.subject_id === s.subject_id);
                    const subjectName = cat?.subject_name ?? `Subject #${s.subject_id}`;
                    const availableGrades = cat?.grades ?? [];
                    return (
                      <Box key={s.subject_id} sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: "10px", p: 1.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy.fg }}>
                            {subjectName}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeSubject(s.subject_id)}
                            aria-label={`Remove ${subjectName}`}
                            sx={{ color: "text.disabled", "&:hover": { color: C.rose.fg } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {availableGrades.length === 0 ? (
                          <Typography sx={{ fontSize: 11.5, color: "text.disabled", fontStyle: "italic" }}>
                            No grades available for this subject.
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {availableGrades.map((g) => {
                              const selected = s.subject_grade_ids.includes(g.subject_grade_id);
                              return (
                                <Chip
                                  key={g.subject_grade_id}
                                  label={formatGradeLabel(g.grade_name)}
                                  size="small"
                                  onClick={() => toggleGrade(s.subject_id, g.subject_grade_id)}
                                  sx={{
                                    height: 24, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                                    bgcolor: selected ? C.purple.bg : "transparent",
                                    color: selected ? C.purple.fg : "text.secondary",
                                    border: `1px solid ${selected ? C.purple.border : "rgba(15,23,42,0.15)"}`,
                                    "&:hover": { bgcolor: selected ? C.purple.bg : "rgba(15,23,42,0.04)" },
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}

                  {/* Add-subject row. Filtered against already-assigned
                      subjects so a duplicate is structurally impossible
                      from the picker (belt-and-braces with the server
                      dedup guard). */}
                  {addableSubjects.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        select fullWidth size="small"
                        value={pendingAddSubjectId}
                        onChange={(e) => setPendingAddSubjectId(e.target.value ? Number(e.target.value) : "")}
                        placeholder="Select Subject"
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" disabled>
                          Select Subject
                        </MenuItem>
                        {addableSubjects.map((s) => (
                          <MenuItem key={s.subject_id} value={s.subject_id}>
                            {s.subject_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        onClick={() => {
                          if (typeof pendingAddSubjectId === "number") {
                            addSubject(pendingAddSubjectId);
                            setPendingAddSubjectId("");
                          }
                        }}
                        disabled={!pendingAddSubjectId}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                      >
                        Add
                      </Button>
                    </Box>
                  )}
                  {draft.subjects.length === 0 && addableSubjects.length === 0 && !optionsLoading && (
                    <Typography sx={{ fontSize: 12.5, color: "text.disabled", fontStyle: "italic" }}>
                      No subjects available.
                    </Typography>
                  )}
                </Stack>
              </Box>
              {payload?.has_aba_caseload && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: C.green.bg, border: `1px solid ${C.green.border}`, borderRadius: "10px", px: 1.5, py: 1 }}>
                    <HealthAndSafetyOutlinedIcon sx={{ fontSize: 18, color: C.green.fg }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.green.fg }}>
                      ABA Caseload
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.green.fg, opacity: 0.85 }}>
                      You have an active ABA teaching load.
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Locations */}
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 14 }} /> Locations
                </Typography>
                {payload?.locations && payload.locations.length > 0 ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {payload.locations.map((l) => grpChip(l.name, "teal"))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: "text.disabled", fontStyle: "italic" }}>
                    {NO_LOC}
                  </Typography>
                )}
              </Box>
              <Divider />
              {/* Subjects & Grades */}
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <SchoolOutlinedIcon sx={{ fontSize: 14 }} /> Subjects &amp; Grades
                </Typography>
                {payload?.teaching_assignments && payload.teaching_assignments.length > 0 ? (
                  <Stack spacing={1.25}>
                    {payload.teaching_assignments.map((subj) => (
                      <Box key={subj.subject_id}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy.fg, mb: 0.5 }}>
                          {subj.subject_name}
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {subj.grades.length === 0
                            ? <Typography sx={{ fontSize: 12, color: "text.disabled", fontStyle: "italic" }}>No grades</Typography>
                            : subj.grades.map((g) => grpChip(formatGradeLabel(g.grade_name), "purple"))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: "text.disabled", fontStyle: "italic" }}>
                    {NO_ASSIGN}
                  </Typography>
                )}
              </Box>
              {payload?.has_aba_caseload && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: C.green.bg, border: `1px solid ${C.green.border}`, borderRadius: "10px", px: 1.5, py: 1 }}>
                    <HealthAndSafetyOutlinedIcon sx={{ fontSize: 18, color: C.green.fg }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.green.fg }}>
                      ABA Caseload
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.green.fg, opacity: 0.85 }}>
                      You have an active ABA teaching load.
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </SectionCard>

        {/* ── Account (always read-only — system-controlled fields) ── */}
        <SectionCard title="Account" icon={<BadgeOutlinedIcon />} tone="amber">
          <Stack divider={<Divider />}>
            <InfoRow label="Username"      value={staff?.username}                  icon={<PersonOutlineIcon />} loading={isLoading} />
            <InfoRow label="Reference ID"  value={staff?.reference_id}              icon={<BadgeOutlinedIcon />} loading={isLoading} />
            <InfoRow label="Portal Status" value={staff ? portalMeta.label : null}  icon={<BadgeOutlinedIcon />} loading={isLoading} />
            <InfoRow label="Last Login"    value={lastLoginLabel}                   icon={<BadgeOutlinedIcon />} loading={isLoading} emptyLabel="Never" />
          </Stack>
        </SectionCard>
      </Box>
    </Box>
  );
};

export default MyProfile;
